// S6 frontend — TanStack Query hooks for ONVIF discovery + protocol.
// Single module mirror of /api/v1/discovery/* and /cameras/{id}/device-info.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface DiscoveredDevice {
  id: string;
  ip_address: string;
  port: number;
  manufacturer: string | null;
  model: string | null;
  firmware_version: string | null;
  serial_number: string | null;
  onvif_profiles: Array<{ token: string; name: string; type: string }>;
  stream_uri: string | null;
  snapshot_uri: string | null;
  device_service_url: string | null;
  mac_address: string | null;
  hardware_id: string | null;
  is_added: boolean;
  camera_id: string | null;
  discovered_at: string;
}

export interface DiscoveryDetail {
  id: string;
  tenant_id: string;
  network_range: string | null;
  status: "pending" | "running" | "complete" | "failed";
  result_count: number;
  error_message: string | null;
  discovered_at: string;
  devices: DiscoveredDevice[];
}

export interface ProbeResult {
  ip_address: string;
  port: number;
  manufacturer: string | null;
  model: string | null;
  firmware_version: string | null;
  serial_number: string | null;
  hardware_id: string | null;
  onvif_profiles: Array<{ token: string; name: string; type: string }>;
  stream_uri: string | null;
  snapshot_uri: string | null;
  device_service_url: string | null;
  error?: string;
}

export interface CameraDeviceInfo {
  protocol: "rtsp" | "onvif" | "isapi";
  manufacturer: string | null;
  model: string | null;
  firmware: string | null;
  serial_number: string | null;
  hardware_id: string | null;
  profiles: Array<{ token: string; name: string; type: string }>;
  stream_uri: string;
  snapshot_uri: string;
}

// ---------------------------------------------------------------------------
// Discovery hooks
// ---------------------------------------------------------------------------
const discoveryKey = ["discovery"] as const;

export function useStartScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      network_range?: string;
      timeout?: number;
    }) => {
      const { data } = await api.post<{ discovery_id: string; status: string }>(
        "/api/v1/discovery/scan",
        payload,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: discoveryKey }),
  });
}

export function useDiscovery(
  id: string | undefined,
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: [...discoveryKey, id],
    queryFn: async () => {
      const { data } = await api.get<DiscoveryDetail>(
        `/api/v1/discovery/${id}`,
      );
      return data;
    },
    enabled: Boolean(id),
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useLatestDiscovery() {
  return useQuery({
    queryKey: [...discoveryKey, "latest"],
    queryFn: async () => {
      const { data } = await api.get<DiscoveryDetail | null>(
        "/api/v1/discovery/results",
      );
      return data;
    },
  });
}

export function useProbeDevice() {
  return useMutation({
    mutationFn: async (payload: {
      ip: string;
      port?: number;
      username?: string;
      password?: string;
    }): Promise<ProbeResult> => {
      const { data } = await api.post<ProbeResult>(
        "/api/v1/discovery/probe",
        payload,
      );
      return data;
    },
  });
}

export function useAddDiscoveredDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      discoveryId,
      deviceId,
      payload,
    }: {
      discoveryId: string;
      deviceId: string;
      payload: {
        name: string;
        site_id?: string;
        entity_id?: string;
        username?: string;
        password?: string;
        profile_id?: string;
        tags?: string[];
      };
    }) => {
      const { data } = await api.post<{
        id: string;
        name: string;
        site_id: string;
        protocol: string;
        onvif_device_uuid: string | null;
      }>(
        `/api/v1/discovery/${discoveryId}/devices/${deviceId}/add`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: discoveryKey });
      qc.invalidateQueries({ queryKey: ["cameras"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Camera device-info hook (S6)
// ---------------------------------------------------------------------------
export function useCameraDeviceInfo(cameraId: string | undefined) {
  return useMutation({
    mutationFn: async (): Promise<CameraDeviceInfo> => {
      const { data } = await api.get<CameraDeviceInfo>(
        `/api/v1/cameras/${cameraId}/device-info`,
      );
      return data;
    },
  });
}
